import type { ReactNode } from 'react';
import { amountText, assetName, type Asset } from '../assets.js';
import { localAmount } from '../localMoney.js';
import { netOfFee } from '../prices.js';
import type { SimpleBook } from '../simpleCap.js';
import type { Estimate, Side, TicketPair } from '../ticket.js';
import { perCoin } from './Market.js';

/**
 * The Simple ticket: buy or sell, one amount, and what it comes to at the
 * best price on the book, after the fee, with the person's own money beneath.
 *
 * The same ticket in two places. On the trading screen it places the order.
 * On the welcome, before any wallet, it answers from the public book and its
 * button makes the wallet, with the ticket kept for afterwards. What differs
 * is passed in: the button and what is under it, the Max link (there is no
 * balance before a wallet), and the way to Advanced.
 */
export function SimpleTicket({
  pair,
  side,
  onSide,
  amount,
  onAmount,
  simple,
  est,
  capped,
  currency,
  rates,
  onMax,
  toAdvanced,
  hint,
  action,
}: {
  pair: TicketPair | undefined;
  side: Side;
  onSide: (s: Side) => void;
  amount: string;
  onAmount: (text: string) => void;
  /** The best price by someone else on the other side, and all there is at it; null when nobody. */
  simple: SimpleBook | null;
  est: Estimate | null;
  /** More was typed than is there, so the order is cut to what is. */
  capped: boolean;
  /** The chosen currency and the day's rates, for the line beneath the total. */
  currency: string | null;
  rates: Record<string, number> | null;
  /** Fills in all the wallet can spend; absent with no wallet, and the link is not shown. */
  onMax?: () => void;
  /** The link to Advanced; null with no wallet yet, and the sentence says so instead. */
  toAdvanced: ReactNode | null;
  /** A line under the total, such as whether a bid earns rewards. */
  hint?: ReactNode;
  /** The button and anything under it. */
  action: ReactNode;
}) {
  const base = (pair?.base ?? 'GAJU') as Asset;
  const quote = (pair?.quote ?? 'USDC') as Asset;
  /** "1,900 XGE", "1,195 Gaju": whole amounts without ".00". */
  const qtyText = (units: bigint): string => amountText(base, units).replace(/\.00 /, ' ');
  const order = side === 'buy' ? 'a bid' : 'an offer';
  const received =
    est === null || pair === undefined ? null : side === 'sell' ? netOfFee(pair, est.q) : est.q;
  return (
    <div className="wt-card" data-testid="wt-simple-ticket">
      <div className="wt-seg" role="tablist">
        <button
          role="tab"
          aria-selected={side === 'buy'}
          className="buy"
          onClick={() => {
            onSide('buy');
          }}
          data-testid="wt-buy"
        >
          Buy {assetName[base]}
        </button>
        <button
          role="tab"
          aria-selected={side === 'sell'}
          className="sell"
          onClick={() => {
            onSide('sell');
          }}
          data-testid="wt-sell"
        >
          Sell {assetName[base]}
        </button>
      </div>
      <label className="wt-field">
        <span className="wt-field-label">
          {side === 'buy' ? 'How much do you want?' : 'How much are you selling?'}
          {onMax !== undefined && (
            <button className="wt-max" onClick={onMax} type="button">
              Max
            </button>
          )}
        </span>
        <span className="wt-input">
          <input
            inputMode="decimal"
            value={amount}
            placeholder="0"
            onChange={(e) => {
              onAmount(e.target.value);
            }}
            data-testid="wt-amount"
          />
          <span className="wt-unit">{assetName[base]}</span>
        </span>
      </label>
      {simple === null ? (
        <p className="wt-note" data-testid="wt-simple-empty">
          {side === 'buy' ? `Nothing on offer right now. ` : `Nobody is bidding right now. `}
          {toAdvanced === null ? (
            `With a wallet you can place ${order} at your own price.`
          ) : (
            <>
              Place {order} at your own price in {toAdvanced}.
            </>
          )}
        </p>
      ) : (
        <>
          {capped && (
            <p className="wt-cap" data-testid="wt-simple-cap">
              Only {qtyText(simple.available)} {side === 'buy' ? 'on offer' : 'wanted'} right now.{' '}
              {toAdvanced === null ? (
                `With a wallet you can ${side === 'buy' ? 'bid for' : 'offer'} more in Advanced.`
              ) : (
                <>
                  To {side === 'buy' ? 'bid for' : 'offer'} more, use {toAdvanced}.
                </>
              )}
            </p>
          )}
          <div className="wt-sum">
            <div>
              <span>{side === 'buy' ? 'You pay about' : 'You get about'}</span>
            </div>
            <div className="wt-big-total" data-testid="wt-total">
              {received === null ? '—' : amountText(quote, received)}
            </div>
            {/* The total is what is signed, so it stays in USDC; this reads it
                out in the chosen currency beneath. */}
            {quote === 'USDC' &&
              received !== null &&
              (() => {
                const text = localAmount(received, currency, rates);
                return text === null ? null : (
                  <div className="wt-sum-local" data-testid="wt-total-local">
                    about {text}
                  </div>
                );
              })()}
            <div className="wt-sum-sub">
              {pair === undefined ? '' : perCoin(pair, simple.price)} each, after the{' '}
              {pair === undefined ? '' : pair.feeBps / 100}% fee.
              {est !== null && est.fee > 0n
                ? ` Plus ${amountText(est.spend, est.fee)} network.`
                : ''}
            </div>
          </div>
          {hint}
        </>
      )}
      {action}
      {simple !== null && (
        /* One line; the rest is in the terms. */
        <div className="wt-note">Held in escrow when you sign; back to you if you cancel.</div>
      )}
    </div>
  );
}
